import type { SocialNetwork } from '../types'

interface SocialUrlConfig {
  /** Affiché devant l'input et préfixé (avec https://) au handle saisi. */
  prefix: string
  /** Domaines reconnus quand on extrait un handle d'une URL collée ou stockée. */
  domains: string[]
}

export const SOCIAL_URL_CONFIGS: Partial<Record<SocialNetwork, SocialUrlConfig>> = {
  facebook: { prefix: 'facebook.com/', domains: ['facebook.com', 'fb.com', 'm.facebook.com'] },
  instagram: { prefix: 'instagram.com/', domains: ['instagram.com'] },
  tiktok: { prefix: 'tiktok.com/@', domains: ['tiktok.com'] },
  twitter: { prefix: 'x.com/', domains: ['x.com', 'twitter.com'] },
  linkedin: { prefix: 'linkedin.com/', domains: ['linkedin.com'] },
  youtube: { prefix: 'youtube.com/@', domains: ['youtube.com'] },
  pinterest: { prefix: 'pinterest.com/', domains: ['pinterest.com', 'pinterest.fr'] },
  whatsapp: { prefix: 'wa.me/', domains: ['wa.me', 'api.whatsapp.com'] }
}

const looksLikeUrl = (value: string) => /^(https?:\/\/|www\.)/i.test(value) || /^[\w-]+(\.[\w-]+)+\//.test(value)

const stripSchemeAndWww = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')

export const matchesSocialDomain = (network: SocialNetwork, url: string): boolean => {
  const config = SOCIAL_URL_CONFIGS[network]
  if (!config) return false
  const lower = stripSchemeAndWww(url).toLowerCase()
  return config.domains.some((d) => lower === d || lower.startsWith(`${d}/`))
}

export const extractSocialHandle = (network: SocialNetwork, url: string): string => {
  const config = SOCIAL_URL_CONFIGS[network]
  if (!config) return url

  const trimmed = url.trim()
  let rest = stripSchemeAndWww(trimmed)
  const lower = rest.toLowerCase()
  const domain = config.domains.find((d) => lower === d || lower.startsWith(`${d}/`))
  if (domain) rest = rest.slice(domain.length)
  // URL d'un autre domaine (ex. switch depuis Website) : on vide plutôt que de
  // fabriquer un faux handle du type instagram.com/monresto.fr/contact.
  else if (looksLikeUrl(trimmed)) return ''

  return rest.replace(/^\//, '').replace(/^@/, '').replace(/\/$/, '')
}

export const buildSocialUrl = (network: SocialNetwork, handle: string): string => {
  const config = SOCIAL_URL_CONFIGS[network]
  if (!config) return handle.trim()

  let cleaned = extractSocialHandle(network, handle)
  if (network === 'whatsapp') {
    // Lien api.whatsapp.com/send?phone=336... : on récupère le numéro du param.
    const phoneParam = cleaned.match(/phone=\+?([\d\s.-]+)/)?.[1]
    if (phoneParam) cleaned = phoneParam
    // wa.me n'accepte qu'un numéro, sauf les codes click-to-chat wa.me/message/XXX.
    if (!cleaned.startsWith('message/')) {
      cleaned = cleaned.replace(/^\+/, '').replace(/[\s.-]/g, '')
      if (/\D/.test(cleaned)) cleaned = ''
    }
  }
  if (!cleaned) return ''

  // Un chemin multi-segments (youtube.com/channel/UC..., tiktok.com/share/...)
  // n'est pas un handle : on ne lui colle pas le @ du préfixe.
  const prefix = cleaned.includes('/') ? config.prefix.replace(/@$/, '') : config.prefix
  return `https://${prefix}${cleaned}`
}
